import { useMutation } from "@tanstack/react-query";
import { apiClient } from "./api-client";

/** Where a sign-in lands when the server named no page of its own. */
const DEFAULT_REDIRECT_TARGET = "/sidekiq";

/** What the sign-in form collects and hands to the server, verbatim. */
export interface OperatorCredentials {
  email: string;
  password: string;
}

/**
 * What `POST /api/v1/operators/sign_in` returns — the signed-in operator's email
 * and the path to navigate to (contracts/operator-session-api.md §1). No id, no
 * timestamps, no token: the session cookie is the credential.
 *
 * Both fields are optional because one benign case answers without them. Devise's
 * `require_no_authentication` turns a re-submit by an already signed-in operator
 * into a 302; the client follows it, sees `response.redirected`, and resolves the
 * non-JSON landing page as an empty session (any other non-JSON 2xx rejects as
 * MalformedResponseError). `resolveRedirectTarget` supplies the destination the
 * body did not.
 */
export interface OperatorSession {
  operator?: { email: string };
  redirectTo?: string;
}

/**
 * Establish the operator session.
 *
 * The body is nested — Devise's strong parameters read `params[:operator][:email]`,
 * so `{ operator: credentials }` is the shape on the wire, unlike the flat body the
 * public signup endpoint takes.
 *
 * Both values are sent as typed. Case-insensitive email matching is the server's job
 * (FR-002) and a password is a literal, so normalizing either here would put a second,
 * drifting normalizer in front of the authoritative one — and could turn a correct
 * password into a failed sign-in.
 *
 * No status is inspected and nothing is caught: the api-client throws `HttpError` on
 * any status >= 400 and a transport failure rejects on its own, so every failure
 * reaches the form as a rejection it can branch on (401 → bad credentials, anything
 * else → connection error).
 */
export async function signInOperator(
  credentials: OperatorCredentials,
): Promise<OperatorSession> {
  const response = await apiClient.post<OperatorSession>(
    "/api/v1/operators/sign_in",
    { operator: credentials },
  );
  return response.data;
}

/**
 * `signInOperator` as a TanStack mutation, giving the sign-in form its pending and
 * error states without hand-rolled state (contracts/protected-surfaces.md §4).
 *
 * Retries are off: a silent retry would hold the form in its pending state well past
 * the point where the operator deserves a message to act on, and re-posting refused
 * credentials cannot succeed anyway.
 */
export function useSignInOperator() {
  return useMutation({
    mutationFn: signInOperator,
    retry: false,
  });
}

/**
 * The open-redirect guard on the server's `redirectTo` (research D10).
 *
 * The form feeds the result straight to `window.location.assign`, so a value that is
 * not an in-site absolute path must collapse to the Sidekiq dashboard rather than
 * navigate the operator off-site. A leading slash alone is not enough: `//evil.example`
 * is protocol-relative and a naive "starts with a slash" check would wave it through.
 */
export function resolveRedirectTarget(redirectTo?: string): string {
  const isInSitePath =
    redirectTo !== undefined &&
    redirectTo.startsWith("/") &&
    !redirectTo.startsWith("//");

  return isInSitePath ? redirectTo : DEFAULT_REDIRECT_TARGET;
}
